<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import TimeRangeSelector from '@/components/cockpit/TimeRangeSelector.vue'
import KpiStrip from '@/components/cockpit/KpiStrip.vue'
import FinancialChartSection from '@/components/cockpit/FinancialChartSection.vue'
import OpsHealthPanel from '@/components/cockpit/OpsHealthPanel.vue'
import LeaderboardSection from '@/components/cockpit/LeaderboardSection.vue'
import ActivityFeed from '@/components/cockpit/ActivityFeed.vue'
import { useCockpitKpis } from '@/composables/cockpit/useCockpitKpis'
import { useCockpitFinancials } from '@/composables/cockpit/useCockpitFinancials'
import { useCockpitLeaderboard } from '@/composables/cockpit/useCockpitLeaderboard'
import { useCockpitOps } from '@/composables/cockpit/useCockpitOps'
import type { TimeRange, FinancialBucket } from '@/types/cockpit'

const { t } = useI18n()

// Global time range — default 30 days (decision 1)
const range = ref<TimeRange>({ key: '30d' })

// Financial bucket toggle (monthly / quarterly)
const financialBucket = ref<FinancialBucket>('monthly')

// Per-zone composables — each zone fails independently
const { kpis, loading: kpisLoading, error: kpisError, fetch: fetchKpis } = useCockpitKpis()
const {
  data: financials,
  loading: financialsLoading,
  error: financialsError,
  fetch: fetchFinancials,
} = useCockpitFinancials()
const {
  entries: leaderboard,
  loading: leaderboardLoading,
  error: leaderboardError,
  fetch: fetchLeaderboard,
} = useCockpitLeaderboard()
const { ops, loading: opsLoading, error: opsError, fetch: fetchOps } = useCockpitOps()

function loadRangeData(): void {
  void fetchKpis(range.value)
  void fetchFinancials(range.value, financialBucket.value)
  void fetchLeaderboard(range.value)
  // Ops is always "now" — not re-fetched when range changes
}

// Re-fetch KPIs, financials, and leaderboard when range changes.
// Ops and the feed do NOT depend on the global range.
watch(range, () => {
  void fetchKpis(range.value)
  void fetchFinancials(range.value, financialBucket.value)
  void fetchLeaderboard(range.value)
})

// Re-fetch financials when the bucket toggle changes.
watch(financialBucket, () => {
  void fetchFinancials(range.value, financialBucket.value)
})

onMounted(() => {
  loadRangeData()
  void fetchOps()
})
</script>

<template>
  <AdminLayout>
    <div class="space-y-8">
      <!-- Page header: title + time-range selector -->
      <div class="flex flex-wrap items-center justify-between gap-4">
        <h1 class="text-2xl font-bold text-white">{{ t('admin.cockpit.title') }}</h1>
        <TimeRangeSelector v-model="range" />
      </div>

      <!-- 1. KPI Strip -->
      <section aria-labelledby="kpi-section-heading">
        <h2 id="kpi-section-heading" class="sr-only">{{ t('admin.cockpit.kpi.section_title') }}</h2>
        <KpiStrip
          :kpis="kpis"
          :loading="kpisLoading"
          :error="kpisError"
          @retry="fetchKpis(range)"
        />
      </section>

      <!-- 2. Financial Chart Section -->
      <section aria-labelledby="finance-section-heading">
        <h2 id="finance-section-heading" class="sr-only">{{ t('admin.cockpit.finance.title') }}</h2>
        <FinancialChartSection
          :data="financials"
          :loading="financialsLoading"
          :error="financialsError"
          :bucket="financialBucket"
          @update:bucket="financialBucket = $event"
          @retry="fetchFinancials(range, financialBucket)"
        />
      </section>

      <!-- 3. Ops / Health Panel -->
      <section aria-labelledby="ops-section-heading">
        <h2 id="ops-section-heading" class="text-lg font-bold text-white mb-4">
          {{ t('admin.cockpit.ops.title') }}
        </h2>
        <OpsHealthPanel
          :ops="ops"
          :loading="opsLoading"
          :error="opsError"
          @retry="fetchOps()"
        />
      </section>

      <!-- 4. Per-organisation Leaderboard -->
      <section aria-labelledby="leaderboard-section-heading">
        <h2 id="leaderboard-section-heading" class="sr-only">{{ t('admin.cockpit.leaderboard.title') }}</h2>
        <LeaderboardSection
          :entries="leaderboard"
          :loading="leaderboardLoading"
          :error="leaderboardError"
          @retry="fetchLeaderboard(range)"
        />
      </section>

      <!-- 5. Activity Feed -->
      <section aria-labelledby="activity-section-heading">
        <h2 id="activity-section-heading" class="sr-only">{{ t('admin.cockpit.activity.title') }}</h2>
        <ActivityFeed />
      </section>
    </div>
  </AdminLayout>
</template>
